import React, { useEffect, useRef, useState } from "react";
import type { FC } from "react";
import { Box, Button, useIsVisible } from "@edreamsodigeo/prisma-design-system";
import { useTracking, useTranslation } from "@edreamsodigeo/frontend-core";

import {
  SubscriptionOfferType,
  SubscriptionOfferPeriod,
} from "~/generated/graphql";
import useTrackPriceEvent from "~/clientState/hooks/useTrackPriceEvent";
import {
  DualPriceTrackingCategoriesEnum,
  type DualPriceStickyBarProps,
} from "./contract";
import { DualPriceTrackingProvider } from "./components/DualPriceTrackingProvider";
import useGetSubscriptionOffers from "~/clientState/hooks/useGetSubscriptionOffers";
import { useFilterOfferByType } from "~/clientState/hooks/useFilterOfferByType";
import { DualPriceStickyContainer } from "~/components/engage/DualPriceStickyBar/components/DualPriceStickyContainer";
import Fade from "~/components/common/animation/Fade";
import { useIsMeta } from "~/clientState/providers/SessionScopeProvider/SessionScopeProvider";

const TRACKING_CATEGORY = DualPriceTrackingCategoriesEnum.FLIGHTS_SUMMARY;

const DualPriceStickyBarCommon: FC<DualPriceStickyBarProps> = ({
  Component = DualPriceStickyContainer,
  onContinueWithFlight,
  itineraryData,
  pricePerPassenger: discountedPricePerPassenger,
  nonDiscountedPricePerPassenger,
  loading,
  isFlightSummary = false,
  trackedPrice,
}) => {
  const { trackEvent } = useTracking();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [initialized, setInitialized] = useState(false);

  const { isVisible: isDualPriceVisible } = useIsVisible(containerRef, {
    threshold: 0.45,
    ref: containerRef,
  });
  const { offers } = useGetSubscriptionOffers({
    offerPeriod: SubscriptionOfferPeriod.FreeTrial,
  });
  const basicFTOffer = useFilterOfferByType({
    offersList: offers,
    subscriptionOfferType: SubscriptionOfferType.Basic,
  });
  const onLoadTrackingEventSent = useRef(false);
  const isMeta = useIsMeta();
  const isFlightSummaryMeta = isMeta || isFlightSummary;

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (containerRef.current && basicFTOffer) {
        setInitialized(true);
      }
    }, 500);

    return (): void => clearTimeout(timeout);
  }, [basicFTOffer]);

  useEffect(() => {
    if (!onLoadTrackingEventSent.current) {
      onLoadTrackingEventSent.current = true;
      trackEvent({
        category: TRACKING_CATEGORY,
        action: "continue_button",
        label: "continue-button:onload_pag:summary",
      });
    }
  }, [trackEvent, onLoadTrackingEventSent]);

  useTrackPriceEvent({
    isFlightSummary: isFlightSummaryMeta,
    nonDiscountedPrice: trackedPrice,
  });

  if (
    loading ||
    !discountedPricePerPassenger ||
    !nonDiscountedPricePerPassenger ||
    !basicFTOffer ||
    !itineraryData
  ) {
    return null;
  }

  const handleScrollToContainer = (): void => {
    trackEvent({
      category: TRACKING_CATEGORY,
      action: "choose-fare",
      label: "choose-fare_click:info_pag:summary",
    });
    containerRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <DualPriceTrackingProvider
      itineraryData={itineraryData}
      trackingCategory={TRACKING_CATEGORY}
    >
      <Fade duration={250} visible={initialized && !isDualPriceVisible}>
        <Box
          bottom={0}
          position={"fixed"}
          width={"100%"}
          backgroundColor={"neutral.0"}
          p={"small"}
        >
          <Button fullWidth onClick={handleScrollToContainer}>
            {t("prime.dualPriceMobile.selectFare")}
          </Button>
        </Box>
      </Fade>
      <div data-testid={"pricesContainer"} ref={containerRef}>
        <Component
          discountedPricePerPassenger={discountedPricePerPassenger}
          nonDiscountedPricePerPassenger={nonDiscountedPricePerPassenger}
          onContinueWithFlight={onContinueWithFlight}
          offer={basicFTOffer}
          isVisible={isDualPriceVisible}
        />
      </div>
    </DualPriceTrackingProvider>
  );
};

export const DualPriceStickyBar: FC<DualPriceStickyBarProps> = (props) => (
  <DualPriceStickyBarCommon {...props} Component={DualPriceStickyContainer} />
);
